import React from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import {
  TenantSettingKey,
  TenantSettings,
  TenantSettingsFields,
} from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { TenantSettingsField } from '@recurrency/core-api-schema/dist/common/tenantSettingsTypes';

import { Alert } from 'components/Alert';

import { pluralize } from 'utils/formatting';
import { objKeys } from 'utils/object';

interface PendingChange {
  key: TenantSettingKey;
  title: string;
  value: string;
  pendingValue: string;
  requiresOvernightSync: boolean;
}

export function PendingSettingsAlert({
  pendingSettings,
  settings,
}: {
  pendingSettings: Partial<TenantSettings>;
  settings: Partial<TenantSettings>;
}) {
  const pendingKeys = objKeys(pendingSettings);

  if (pendingKeys.length === 0) {
    return null;
  }

  const getValueLabel = (field: TenantSettingsField, value: Any): string => {
    if ('options' in field) {
      const option = field.options.find((o) => o.value === value);
      if (option) return option.label;
    }
    return typeof value === 'string' ? value : JSON.stringify(value);
  };

  const pendingChanges = pendingKeys
    // gracefully ignore unknown setting key due to schema mismatch
    .filter((key) => !!TenantSettingsFields[key])
    .map(
      (key) =>
        ({
          key,
          title: TenantSettingsFields[key].title,
          value: getValueLabel(TenantSettingsFields[key], settings[key]),
          pendingValue: getValueLabel(TenantSettingsFields[key], pendingSettings[key]),
          requiresOvernightSync: TenantSettingsFields[key].requiresForecastingTransforms || false,
        } as PendingChange),
    )
    // pendingValue not null generally means there's a pending backend reset job
    // a new setting will have pendingValue and value set to the same
    .filter((change) => !!change.pendingValue);

  if (pendingChanges.length === 0) {
    return null;
  }

  const overnightPendingChanges = pendingChanges.filter((change) => change.requiresOvernightSync);
  const immediatePendingChanges = pendingChanges.filter((change) => !change.requiresOvernightSync);

  const renderPendingChanges = (changes: PendingChange[], updateFrequency: string) =>
    changes.length > 0 && (
      <div>
        <span>
          {pluralize(changes.length, 'pending change', 'pending changes', true)} will update {updateFrequency}.
        </span>
        <ul>
          {changes.map((change, idx) => (
            <li key={idx}>
              {change.pendingValue !== change.value
                ? `${change.title}: ${change.value} → ${change.pendingValue}`
                : // If a setting is being re-run pending value can be equal to value
                  // This avoids the confusing UI true -> true pending
                  `Updating ${change.title} to ${change.value}`}
            </li>
          ))}
        </ul>
      </div>
    );

  return (
    <Alert
      className={css`
        margin-top: 24px;
      `}
      icon={<ExclamationCircleOutlined />}
      message={
        <>
          {renderPendingChanges(immediatePendingChanges, 'in a few minutes')}
          {renderPendingChanges(
            overnightPendingChanges,
            'overnight as it requires significant forecasting recalculations',
          )}
        </>
      }
      type="info"
      banner
    />
  );
}
